// navTargets — centralized typed nav targets + URL helpers, extracted from ContextChain.tsx
// (48.23 split; the component grew past the size watchdog). PURE module: no React, no state —
// just the {to, params} builders. ContextChain re-exports these, so every existing
// `import { navTo, buildAppUrl } from "./ContextChain"` site keeps working unchanged.
//
// TanStack {to, params} per route depth, so the param-shape mapping (which params each route
// depth needs) lives in ONE place. This is the typed-route payoff: the route literals are
// checked against the createRoute tree at compile time.
export const navTo = {
  workspaceHome: (ws: string) => ({ to: "/w/$ws/home" as const, params: { ws } }),
  clientHome: (ws: string, client: string) => ({
    to: "/w/$ws/c/$client/home" as const,
    params: { ws, client },
  }),
  // A client-LESS engagement (cross-client host, A5/A6) routes WITHOUT the /c/$client segment.
  engagementHome: (ws: string, client: string, eng: string) =>
    client
      ? { to: "/w/$ws/c/$client/e/$eng/home" as const, params: { ws, client, eng } }
      : { to: "/w/$ws/e/$eng/home" as const, params: { ws, eng } },
  // Section targets at a client-less engagement (no /c/$client). seg = "/data" | "/decisions" | …
  wsEngagementSection: (ws: string, eng: string, seg: string) =>
    ({ to: `/w/$ws/e/$eng${seg}` as const, params: { ws, eng } }),
  // A project inside a client-less HOST engagement (48.23) routes WITHOUT the /c/$client segment
  // — same branch rule as engagementHome above.
  projectHome: (ws: string, client: string, eng: string, project: string) =>
    client
      ? { to: "/w/$ws/c/$client/e/$eng/p/$project/home" as const, params: { ws, client, eng, project } }
      : { to: "/w/$ws/e/$eng/p/$project/home" as const, params: { ws, eng, project } },
  settings: (ws: string) => ({ to: "/w/$ws/settings" as const, params: { ws } }),
  userManagement: (ws: string) => ({ to: "/w/$ws/admin/users" as const, params: { ws } }),
  missionControl: (ws: string) => ({ to: "/w/$ws/admin/mission-control" as const, params: { ws } }),
  sandbox: (ws: string) => ({ to: "/w/$ws/sandbox" as const, params: { ws } }),
  myWork: (ws: string) => ({ to: "/w/$ws/mywork" as const, params: { ws } }),
  search: (ws: string) => ({ to: "/w/$ws/search" as const, params: { ws } }),
  notifications: (ws: string) => ({ to: "/w/$ws/notifications" as const, params: { ws } }),
  profile: (ws: string) => ({ to: "/w/$ws/profile" as const, params: { ws } }),
  // Scope-aware destinations (shell rail, 2026-06-29). Workspace-level:
  data: (ws: string) => ({ to: "/w/$ws/data" as const, params: { ws } }),
  activity: (ws: string) => ({ to: "/w/$ws/activity" as const, params: { ws } }),
  knowledge: (ws: string) => ({ to: "/w/$ws/knowledge" as const, params: { ws } }),
  // Client-level (wall-scoped):
  clientEngagements: (ws: string, client: string) => ({ to: "/w/$ws/c/$client/engagements" as const, params: { ws, client } }),
  clientData: (ws: string, client: string) => ({ to: "/w/$ws/c/$client/data" as const, params: { ws, client } }),
  clientDecisions: (ws: string, client: string) => ({ to: "/w/$ws/c/$client/decisions" as const, params: { ws, client } }),
  clientIntel: (ws: string, client: string) => ({ to: "/w/$ws/c/$client/intel" as const, params: { ws, client } }),
  clientGovernance: (ws: string, client: string) => ({ to: "/w/$ws/c/$client/governance" as const, params: { ws, client } }),
  // V2 multi-level mounting: the app mounts at the DEEPEST present scope segment. Pass "" for absent
  // levels (e.g. a client-level mount has eng="" project="") and the matching per-level route is chosen.
  app: (ws: string, client: string, eng: string, project: string, app: string) => {
    if (project) {
      // Host-engagement projects (48.23) mount apps without the /c segment.
      return client
        ? { to: "/w/$ws/c/$client/e/$eng/p/$project/apps/$app" as const,
            params: { ws, client, eng, project, app } }
        : { to: "/w/$ws/e/$eng/p/$project/apps/$app" as const,
            params: { ws, eng, project, app } };
    }
    if (eng) {
      return client
        ? { to: "/w/$ws/c/$client/e/$eng/apps/$app" as const, params: { ws, client, eng, app } }
        : { to: "/w/$ws/e/$eng/apps/$app" as const, params: { ws, eng, app } };
    }
    if (client) {
      return { to: "/w/$ws/c/$client/apps/$app" as const, params: { ws, client, app } };
    }
    return { to: "/w/$ws/apps/$app" as const, params: { ws, app } };
  },
  // ── Generic node-addressable family (spec 48.24 §6) — a node is addressed by its ID, not a
  // positional level path, so FREE shapes (client under engagement, project under client, …) get a
  // lossless URL. The typed routes above remain deep-link aliases; shell-emitted navigation moves to
  // these. Scope is derived from the node's ancestry (nodeAncestry), never the URL segments.
  nodeHome: (ws: string, node: string) => ({ to: "/w/$ws/n/$node/home" as const, params: { ws, node } }),
  nodeSection: (ws: string, node: string, section: string) =>
    ({ to: "/w/$ws/n/$node/$section" as const, params: { ws, node, section } }),
  nodeApp: (ws: string, node: string, app: string) =>
    ({ to: "/w/$ws/n/$node/apps/$app" as const, params: { ws, node, app } }),
};

// Back-compat app-target helper used by SideNav — now level-aware via navTo.app (deepest present level).
// The artifact-path splat is deferred to Phase 1/2 (when SubAppPage hosts a real app).
export function buildAppUrl(ws: string, client: string, eng: string, project: string, app: string) {
  return navTo.app(ws, client, eng, project, app);
}

export function parseSegmentsFromPath(splat: string | undefined): string[] {
  if (!splat) return [];
  const parts = splat.split("/").filter(Boolean);
  const out: string[] = [];
  for (let i = 0; i < parts.length; i += 2) {
    if (parts[i] === "a" && parts[i + 1]) out.push(decodeURIComponent(parts[i + 1]!));
  }
  return out;
}
